  import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm, useController } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select, { components } from "react-select";
import useAuth from "./hooks/useAuth";
import Captcha from "./Components/Captcha";
import { createPortal } from "react-dom";

/** =========================
 *  Constants / helpers
 *  ========================= */

const USERNAME_MIN = 2;
const USERNAME_MAX = 20;

const getCharacterValidationError = (string) =>
  `Your password must have at least 1 ${string} character`;

const cleanEmail = (s) =>
  typeof s === "string"
    ? s.replace(/[\u200B-\u200D\uFEFF\u00A0]/g, "").trim().toLowerCase()
    : s;

const cleanText = (s) => (typeof s === "string" ? s.trim() : s);

// Expanded language list (flags are emoji; these are not perfect country↔language mappings, but work well UX-wise)
const languageOptionsRaw = [
  { value: "en", label: "English", flag: "🇬🇧" },
  { value: "en-US", label: "English (US)", flag: "🇺🇸" },
  { value: "no", label: "Norwegian", flag: "🇳🇴" },
  { value: "sv", label: "Swedish", flag: "🇸🇪" },
  { value: "da", label: "Danish", flag: "🇩🇰" },
  { value: "fi", label: "Finnish", flag: "🇫🇮" },
  { value: "is", label: "Icelandic", flag: "🇮🇸" },

  { value: "de", label: "German", flag: "🇩🇪" },
  { value: "nl", label: "Dutch", flag: "🇳🇱" },
  { value: "fr", label: "French", flag: "🇫🇷" },
  { value: "es", label: "Spanish", flag: "🇪🇸" },
 
  
  { value: "it", label: "Italian", flag: "🇮🇹" },
  { value: "pt", label: "Portuguese", flag: "🇵🇹" },
  { value: "pt-BR", label: "Portuguese (Brazil)", flag: "🇧🇷" },
  { value: "ro", label: "Romanian", flag: "🇷🇴" },
  { value: "el", label: "Greek", flag: "🇬🇷" },
  { value: "hu", label: "Hungarian", flag: "🇭🇺" },
  { value: "cs", label: "Czech", flag: "🇨🇿" },
  { value: "sk", label: "Slovak", flag: "🇸🇰" },
  { value: "pl", label: "Polish", flag: "🇵🇱" },
  { value: "uk", label: "Ukrainian", flag: "🇺🇦" },
  { value: "ru", label: "Russian", flag: "🇷🇺" },
  { value: "bg", label: "Bulgarian", flag: "🇧🇬" },
  { value: "sr", label: "Serbian", flag: "🇷🇸" },
  { value: "hr", label: "Croatian", flag: "🇭🇷" },

  { value: "tr", label: "Turkish", flag: "🇹🇷" },
  { value: "ar", label: "Arabic", flag: "🇸🇦" },
  { value: "he", label: "Hebrew", flag: "🇮🇱" },
  { value: "fa", label: "Persian (Farsi)", flag: "🇮🇷" },

  { value: "hi", label: "Hindi", flag: "🇮🇳" },
  { value: "bn", label: "Bengali", flag: "🇧🇩" },
  { value: "ur", label: "Urdu", flag: "🇵🇰" },

  { value: "zh", label: "Chinese (Mandarin)", flag: "🇨🇳" },
  { value: "yue", label: "Chinese (Cantonese)", flag: "🇭🇰" },
  { value: "ja", label: "Japanese", flag: "🇯🇵" },
  { value: "ko", label: "Korean", flag: "🇰🇷" },
  { value: "th", label: "Thai", flag: "🇹🇭" },
  { value: "vi", label: "Vietnamese", flag: "🇻🇳" },
  { value: "id", label: "Indonesian", flag: "🇮🇩" },
  { value: "ms", label: "Malay", flag: "🇲🇾" },

  { value: "sw", label: "Swahili", flag: "🇰🇪" },
];

const languageOptions = [...languageOptionsRaw].sort((a, b) =>
  a.label.localeCompare(b.label)
);

const rolesList = [
  { value: "USER", label: "User" },
  { value: "ADMIN", label: "Admin" },
  { value: "CREATOR", label: "Creator" },
];

// react-select flag renderers
const LanguageOption = (props) => (
  <components.Option {...props}>
    <span className="mr-2">{props.data.flag}</span>
    {props.data.label}
  </components.Option>
);

const LanguageSingleValue = (props) => (
  <components.SingleValue {...props}>
    <span className="mr-2">{props.data.flag}</span>
    {props.data.label}
  </components.SingleValue>
);

const LanguageMultiValueLabel = (props) => (
  <components.MultiValueLabel {...props}>
    <span className="mr-1">{props.data.flag}</span>
    {props.data.label}
  </components.MultiValueLabel>
);

// Minimal shared react-select styles (matches your current style approach)
const selectStyles = {
  control: (base, state) => ({
    ...base,
    border: "1px solid #d1d5db",
    borderRadius: "0.5rem",
    minHeight: "40px",
    fontSize: "14px",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.1)" : "none",
    borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
    "&:hover": {
      borderColor: state.isFocused ? "#3b82f6" : "#9ca3af",
    },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "14px",
    backgroundColor: state.isSelected
      ? "#3b82f6"
      : state.isFocused
      ? "#eff6ff"
      : "white",
    color: state.isSelected ? "white" : "#374151",
    "&:active": {
      backgroundColor: "#3b82f6",
      color: "white",
    },
  }),
};

// Yup schema (aligned to backend username 2–20 and email sanitization)
const schema = yup.object({
  username: yup
    .string()
    .transform((v) => cleanText(v))
    .required("Please enter username")
    .min(USERNAME_MIN, `Username must be at least ${USERNAME_MIN} characters`)
    .max(USERNAME_MAX, `Username must be at most ${USERNAME_MAX} characters`),

  email: yup
    .string()
    .transform((v) => cleanEmail(v))
    .email("Please enter valid email")
    .required("Please enter email"),

  password: yup
    .string()
    .required("Please enter a password")
    .min(8, "Password must have at least 8 characters")
    .matches(/[0-9]/, getCharacterValidationError("digit"))
    .matches(/[a-z]/, getCharacterValidationError("lowercase"))
    .matches(/[A-Z]/, getCharacterValidationError("uppercase")),

  confirmPassword: yup
    .string()
    .required("Please re-type your password")
    .oneOf([yup.ref("password")], "Passwords do not match"),

  role: yup.string().nullable().required("Please select role"),

  // NEW optional fields
  l1: yup.string().nullable().optional(),
  otherLanguages: yup.array().of(yup.string()).default([]),

  gender: yup.string().nullable().optional(),
  age: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue == null ? null : Number(originalValue)
    )
    .nullable()
    .optional(),
});

/** =========================
 *  Helper component: free-text language add
 *  ========================= */

function FreeTextLangAdd({ values, onAdd, onRemove }) {
  const [text, setText] = useState("");

  const add = () => {
    const v = cleanText(text);
    if (!v) return;
    if ((values || []).includes(v)) return;
    onAdd(v);
    setText("");
  };

  return (
    <div className="mt-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Type a language and press Enter"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
        />
        <button
          type="button"
          onClick={add}
          className="px-3 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700"
        >
          Add
        </button>
      </div>

      {(values || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {values.map((v) => (
            <span
              key={v}
              className="text-xs bg-gray-100 border border-gray-200 rounded-full px-2 py-1"
            >
              {v}
              <button
                type="button"
                onClick={() => onRemove(v)}
                className="ml-2 text-gray-500 hover:text-gray-800"
                aria-label={`Remove ${v}`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** =========================
 *  Register component
 *  ========================= */

export default function Register({ successHandler }) {
  const [apiError, setApiError] = useState(null);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLangModal, setShowLangModal] = useState(false);

  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

useEffect(() => {
  setMounted(true);
}, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      otherLanguages: [],
      l1: null,
      role: null,
      gender: null,
      age: null,
    },
    mode: "onSubmit",
  });

  // RHF controllers
  const {
    field: { value: roleValue, onChange: roleOnChange, ...restRoleField },
  } = useController({ name: "role", control });

  const {
    field: { value: l1Value, onChange: l1OnChange },
  } = useController({ name: "l1", control });

  const {
    field: { value: otherLangsValue, onChange: otherLangsOnChange },
  } = useController({ name: "otherLanguages", control });

  const usernameValue = watch("username") || "";

  const verifyCaptcha = () => setCaptchaVerified(true);

  const getUserData = async (token) => {
    const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/me`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const userData = await response.json();
      userData.token = token;
      setAuth(userData);
      localStorage.setItem("token", token);
      setApiError(null);

      if (successHandler) successHandler(false);
      else navigate("/");
    } else {
      setApiError("Login succeeded but fetching profile failed.");
    }
  };

  const onFormSubmit = async (data) => {
    setIsLoading(true);
    setApiError(null);

    try {
      const payload = {
        ...data,
        username: cleanText(data.username),
        email: cleanEmail(data.email),
        otherLanguages: Array.isArray(data.otherLanguages) ? data.otherLanguages : [],
      };

      const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const responseLogin = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: payload.email,
            password: data.password,
          }),
        });

        if (!responseLogin.ok) {
          const err = await responseLogin.json().catch(() => null);
          setApiError(err?.detail || "Login failed after registration.");
          setAuth(null);
          return;
        }

        const token = await responseLogin.json();
        await getUserData(token.token);
        return;
      }

      const err = await response.json().catch(() => null);

      if (response.status === 422) {
        setApiError(
          err?.detail
            ? typeof err.detail === "string"
              ? err.detail
              : JSON.stringify(err.detail)
            : "Validation failed. Please check your inputs."
        );
      } else if (response.status === 409) {
        setApiError(err?.detail || "User already exists.");
      } else {
        setApiError(err?.detail || "Registration failed. Please try again.");
      }

      setAuth(null);
    } catch (error) {
      setApiError("Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const onErrors = (errs) => console.error(errs);

  const l1Label = l1Value
    ? languageOptions.find((x) => x.value === l1Value)?.label || l1Value
    : null;

  const otherLabels =
    (otherLangsValue || []).map((v) => languageOptions.find((x) => x.value === v)?.label || v) || [];

  // UX state helpers
  const hasLangInfo = Boolean(l1Value) || Boolean(otherLangsValue?.length);
  const statusPillClass = hasLangInfo
    ? "bg-white/70 border-blue-200 text-blue-800"
    : "bg-white/50 border-blue-100 text-gray-700";

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-xl font-bold text-gray-800 mb-2">Create Your Account</h2>
        <p className="text-sm text-gray-600">Join The Catchphraser</p>
      </div>

      {/* Back Button */}
      <button
        onClick={() => {
          if (successHandler) successHandler(false);
        }}
        type="button"
        className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Login
      </button>

      {!captchaVerified && <Captcha func={verifyCaptcha} />}

      {captchaVerified && (
        <div className="space-y-4">
          <form onSubmit={handleSubmit(onFormSubmit, onErrors)} className="space-y-3">
            {/* Username Field */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Username{" "}
                <span className="text-gray-400">
                  ({USERNAME_MIN}–{USERNAME_MAX} characters)
                </span>
              </label>
              <input
                type="text"
                placeholder="Enter your username"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                autoComplete="username"
                {...register("username")}
              />

              {/* Live warnings */}
              {usernameValue.length > 0 &&
                cleanText(usernameValue).length > 0 &&
                cleanText(usernameValue).length < USERNAME_MIN &&
                !errors?.username && (
                  <p className="mt-1 text-xs text-amber-600">
                    Username is too short ({cleanText(usernameValue).length}/{USERNAME_MIN})
                  </p>
                )}

              {cleanText(usernameValue).length > USERNAME_MAX && !errors?.username && (
                <p className="mt-1 text-xs text-amber-600">
                  Username is too long ({cleanText(usernameValue).length}/{USERNAME_MAX})
                </p>
              )}

              {errors?.username && <p className="mt-1 text-xs text-red-600">{errors.username.message}</p>}
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                placeholder="email@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                autoComplete="email"
                {...register("email")}
              />
              {errors?.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                placeholder="Create a strong password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                autoComplete="new-password"
                {...register("password")}
              />
              {errors?.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                placeholder="Re-enter your password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
                autoComplete="new-password"
                {...register("confirmPassword")}
              />
              {errors?.confirmPassword && (
                <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Account Type</label>
              <Select
                isClearable
                options={rolesList}
                value={roleValue ? rolesList.find((x) => x.value === roleValue) : null}
                onChange={(option) => roleOnChange(option ? option.value : null)}
                {...restRoleField}
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Select account type..."
                styles={selectStyles}
              />
              {errors?.role && <p className="mt-1 text-xs text-red-600">{errors.role.message}</p>}
            </div>

            {/* ✅ UX-polished language personalization card */}
            <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-4 shadow-sm">
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className="h-9 w-9 rounded-xl bg-white/80 border border-blue-100 flex items-center justify-center shadow-sm mt-0.5">
                  <svg className="w-5 h-5 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 5.5a18.02 18.02 0 005.564 4.5M12 20l3-9m0 0l3-9m-3 9H9"
                    />
                  </svg>
                </div>

                <div className="min-w-0 flex-1">
                  {/* Title + status */}
                  <div className="flex items-baseline justify-between gap-2">
                    <h4 className="text-sm font-bold text-gray-900 leading-tight">
                      Tailored to your mother tongue
                    </h4>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusPillClass}`}>
                      {hasLangInfo ? "Personalized" : "Optional"}
                    </span>
                  </div>

                  {/* Value proposition */}
                  <p className="mt-2 text-xs leading-relaxed text-gray-700">
                    Share your L1 and other languages so we can prioritize the sounds and rhythm patterns that learners
                    with your background typically struggle with.
                  </p>

                  {/* Micro-proof (compact) */}
                  <div className="mt-3 space-y-1.5">
                    <div className="flex items-start gap-2 text-xs text-gray-800">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-white/80 border border-blue-100 flex items-center justify-center text-blue-700">
                        ✓
                      </span>
                      <p>More relevant drills (minimal pairs, stress, vowels/consonants).</p>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-gray-800">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-white/80 border border-blue-100 flex items-center justify-center text-blue-700">
                        ✓
                      </span>
                      <p>Less generic practice — faster progress with targeted feedback.</p>
                    </div>
                  </div>

                  {/* Strong CTA (no “floating right”) */}
                  <button
                    type="button"
                    onClick={() => setShowLangModal(true)}
                    className="
                      mt-3 w-full inline-flex items-center justify-center gap-2
                      rounded-xl border border-blue-200 bg-white/80
                      px-3 py-2 text-xs font-semibold text-blue-800
                      hover:bg-white hover:border-blue-300
                      transition shadow-sm
                    "
                  >
                    {hasLangInfo ? "Update language background" : "Set language background"}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Selected summary (only when set) */}
                  {hasLangInfo && (
                    <div className="mt-3 rounded-xl bg-white/70 border border-blue-100 p-3 text-xs text-gray-900 space-y-1">
                      {l1Value && (
                        <div>
                          <span className="font-semibold">Mother tongue:</span> {l1Label}
                        </div>
                      )}
                      {otherLangsValue?.length > 0 && (
                        <div>
                          <span className="font-semibold">Other languages:</span> {otherLabels.join(", ")}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Optional reassurance (only when not set) */}
                  {!hasLangInfo && (
                    <p className="mt-2 text-[11px] text-gray-600">
                      You can skip this now and set it later — but adding it helps us tailor training from day one.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-green-300 disabled:to-green-400 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:scale-100 shadow-md flex items-center justify-center text-sm"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Creating Account...
                </>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          {/* Privacy Policy */}
          <div className="text-center border-t border-gray-200 pt-3">
            <p className="text-xs text-gray-600">
              By creating an account, you agree to our{" "}
              <Link
                className="text-blue-600 hover:text-blue-800 font-medium underline"
                target="_blank"
                rel="noopener noreferrer"
                to="/policy"
              >
                Privacy Policy
              </Link>
            </p>
          </div>

          {/* Error Display */}
          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="stroke-current flex-shrink-0 h-4 w-4 text-red-600 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-red-700 text-xs font-medium break-words">{apiError}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Languages Modal */}
     {mounted && showLangModal
  ? createPortal(
      <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/40 p-4">
        <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800">Language background</h3>
              <button
                type="button"
                onClick={() => setShowLangModal(false)}
                className="text-sm text-gray-500 hover:text-gray-800"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              This helps us tailor exercises (sounds, minimal pairs, and patterns) to your language background.
            </p>
          </div>

          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Mother tongue (L1)
              </label>
              <Select
                isClearable
                options={languageOptions}
                components={{ Option: LanguageOption, SingleValue: LanguageSingleValue }}
                value={l1Value ? languageOptions.find((x) => x.value === l1Value) : null}
                onChange={(opt) => l1OnChange(opt ? opt.value : null)}
                placeholder="Select your mother tongue..."
                classNamePrefix="react-select"
                styles={selectStyles}
              />

              <p className="text-[11px] text-gray-500 mt-1">
                If your language isn’t listed, type it below.
              </p>

              <input
                type="text"
                placeholder="Type your mother tongue (optional)"
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                value={
                  typeof l1Value === "string" &&
                  l1Value.length > 0 &&
                  !languageOptions.some((o) => o.value === l1Value)
                    ? l1Value
                    : ""
                }
                onChange={(e) => l1OnChange(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Other languages you speak
              </label>
              <Select
                isMulti
                isClearable
                options={languageOptions}
                components={{ Option: LanguageOption, MultiValueLabel: LanguageMultiValueLabel }}
                value={(otherLangsValue || []).map(
                  (v) =>
                    languageOptions.find((x) => x.value === v) || {
                      value: v,
                      label: v,
                      flag: "🏳️",
                    }
                )}
                onChange={(opts) => otherLangsOnChange((opts || []).map((o) => o.value))}
                placeholder="Select other languages..."
                classNamePrefix="react-select"
                styles={selectStyles}
              />

              <p className="text-[11px] text-gray-500 mt-1">
                You can also add a language manually below.
              </p>

              <FreeTextLangAdd
                values={otherLangsValue || []}
                onAdd={(lang) => otherLangsOnChange([...(otherLangsValue || []), lang])}
                onRemove={(lang) =>
                  otherLangsOnChange((otherLangsValue || []).filter((x) => x !== lang))
                }
              />
            </div>
          </div>

          <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowLangModal(false)}
              className="px-3 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
            >
              Done
            </button>
          </div>
        </div>
      </div>,
      document.body
    )
  : null}
    </div>
  );
}